import {ReactNode} from 'react';
import {createRoot} from 'react-dom/client';

/**
 * A modal dialog component for React
 * Uses a callback-based approach to handle the result
 *
 * @param renderFn - Function that receives onClose callback and returns ReactNode to render
 * @param resultHandler - Optional callback that receives the result when dialog is closed
 */
export const showImmediate = <T, >(
    renderFn: (onClose: (result?: T) => void, portalRoot?: HTMLElement) => ReactNode,
    resultHandler?: (result: T) => void
): void => {
    // Create a div element to mount the modal
    const containerDiv = document.createElement('div');
    // Marks this host so the client-update gate (lib/updateGate.ts) can tell an
    // open immediate dialog from the menus and popovers that share the portal
    // root (OWA-827, CXR-7-002). Removed with the container on close.
    containerDiv.dataset.immediateDialog = 'open';
    const parentRoot = document.getElementById('portal-root');
    // containerDiv.className = 'confirm-alert-container';
    parentRoot.appendChild(containerDiv);

    // Create a root using the modern React 18+ API
    const root = createRoot(containerDiv);

    // Track if already closed to prevent double-cleanup
    let isClosed = false;

    // Function to handle closing and cleanup with a result
    const closeWithResult = (result: T) => {
        // Prevent double-cleanup which can cause errors during test cleanup
        if (isClosed) return;
        isClosed = true;

        // Check if DOM is still valid before attempting cleanup
        // This prevents errors when tests clear the DOM before dialogs can close
        // Also check if document.body has meaningful content (tests clear innerHTML)
        const isValidDom = document.body.innerHTML &&
                          containerDiv.parentNode &&
                          document.body.contains(containerDiv) &&
                          parentRoot &&
                          document.body.contains(parentRoot);

        if (isValidDom) {
            // Unmount using the root's unmount method
            try {
                root.unmount();
            }
            catch (e) {
                // Already unmounted (e.g., during test cleanup), ignore
            }

            // Remove the container div from the DOM
            try {
                if (containerDiv.parentNode && containerDiv.parentNode.contains(containerDiv)) {
                    containerDiv.parentNode.removeChild(containerDiv);
                }
            }
            catch (e) {
                // DOM already cleaned up, ignore
            }
        }

        // Call the result handler callback if provided
        if (resultHandler) {
            resultHandler(result);
        }
    };

    // Create a wrapper component to handle modal behavior
    // const ModalWrapper: React.FC = () => {
    //     // Handle ESC key
    //     React.useEffect(() => {
    //         const handleKeyDown = (event: KeyboardEvent) => {
    //             if (event.key === 'Escape') {
    //                 closeWithResult(undefined as unknown as T);
    //             }
    //         };
    //
    //         window.addEventListener('keydown', handleKeyDown);
    //         document.body.style.overflow = 'hidden';
    //
    //         return () => {
    //             window.removeEventListener('keydown', handleKeyDown);
    //             document.body.style.overflow = '';
    //         };
    //     }, []);
    //
    //     return (
    //             renderFn(closeWithResult, containerDiv)
    //     );
    // };

    // Render the modal
    // root.render(<ModalWrapper/>);
    root.render(renderFn(closeWithResult, containerDiv));
};

export default showImmediate;